/**
 * VoiceQuickActions Component
 * Icon-only quick action buttons for voice and text modes
 * Positioned on the left side of the input area
 */

import { Croissant, GlassWater, IceCream, UtensilsCrossed } from 'lucide-react';
import type { Language } from './VoiceOrderingContainer';

interface VoiceQuickActionsProps {
  onActionClick: (message: string) => void;
  language: Language;
  disabled?: boolean;
}

interface QuickAction {
  icon: React.ReactNode;
  message: { en: string; ar: string };
  label: { en: string; ar: string };
  ariaLabel: { en: string; ar: string };
}

export function VoiceQuickActions({
  onActionClick,
  language,
  disabled = false,
}: VoiceQuickActionsProps) {
  const actions: QuickAction[] = [
    {
      icon: <Croissant className="h-6 w-6" />,
      message: { 
        en: 'I need breakfast recommendations',
        ar: 'أريد توصيات للإفطار'
      },
      label: { en: 'Breakfast', ar: 'إفطار' },
      ariaLabel: { en: 'Breakfast', ar: 'إفطار' },
    },
    {
      icon: <UtensilsCrossed className="h-6 w-6" />,
      message: { 
        en: 'Show me lunch and dinner recommendations',
        ar: 'أعطني توصيات للغداء والعشاء'
      },
      label: { en: 'Meals', ar: 'وجبات' },
      ariaLabel: { en: 'Meals (Lunch & Dinner)', ar: 'وجبات (غداء وعشاء)' },
    },
    {
      icon: <GlassWater className="h-6 w-6" />,
      message: { 
        en: 'Show me drinks',
        ar: 'أعطني المشروبات'
      },
      label: { en: 'Drinks', ar: 'مشروبات' },
      ariaLabel: { en: 'Drinks', ar: 'مشروبات' },
    },
    {
      icon: <IceCream className="h-6 w-6" />,
      message: { 
        en: 'I need dessert recommendations',
        ar: 'أريد توصيات للحلويات'
      },
      label: { en: 'Desserts', ar: 'حلويات' },
      ariaLabel: { en: 'Desserts', ar: 'حلويات' },
    },
  ];

  return (
    <div className="flex items-center justify-center gap-2 flex-nowrap">
      {actions.map((action, index) => {
        const label = language === 'ar' ? action.label.ar : action.label.en;
        return (
          <button
            key={index}
            onClick={() => !disabled && onActionClick(language === 'ar' ? action.message.ar : action.message.en)}
            disabled={disabled}
            className="flex items-center justify-center rounded-lg transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              width: '44px',
              height: '44px',
              minWidth: '44px',
              minHeight: '44px',
              background: disabled ? 'var(--border)' : 'var(--card-bg)',
              color: disabled ? 'var(--text-secondary)' : 'var(--text-primary)',
              border: `1.5px solid ${disabled ? 'var(--border)' : 'var(--border)'}`,
              boxShadow: disabled ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
            aria-label={language === 'ar' ? action.ariaLabel.ar : action.ariaLabel.en}
            title={label}
          >
            {action.icon}
          </button>
        );
      })}
    </div>
  );
}

